import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  ValidationErrors,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatSelectModule } from '@angular/material/select';
import { CompanyInvitationStatus, CompanyUser } from '@/model/company';
import { GraphqlService } from '@/service/graphql.service';
import { UserService } from '@/service/user.service';
import { Category } from '@/model/category';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-company-edit-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatSelectModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './company-edit-dialog.component.html',
  styleUrl: './company-edit-dialog.component.scss',
  standalone: true,
})
export class CompanyEditDialogComponent {
  form: FormGroup;
  availableCategories: Category[] = [];
  getCategoryName = (id: string): string | undefined =>
    this.availableCategories.find((c) => c.id === id)?.name;
  private lastValidatedEmail = '';
  // When editing an existing company, the email field starts locked (disabled)
  // and can be manually enabled by the user to re-activate validation.
  emailEditingEnabled = false;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly dialogRef: MatDialogRef<CompanyEditDialogComponent>,
    private readonly graphqlService: GraphqlService,
    private readonly userService: UserService,
    @Inject(MAT_DIALOG_DATA) readonly data: { company?: CompanyUser } | null,
  ) {
    this.form = this.formBuilder.group({
      companyName: ['', [Validators.required, Validators.minLength(2)]],
      contactPersonName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      uid: [''],
      website: ['', Validators.required],
      phone: ['', Validators.required],
      address: this.formBuilder.group({
        streetAndNumber: ['', Validators.required],
        city: ['', Validators.required],
        postalCode: ['', Validators.required],
      }),
      categoryIds: [[], [Validators.required]],
    });

    // Async email availability validation
    this.form
      .get('email')!
      .valueChanges.subscribe((value: string) => this.validateEmail(value));

    this.graphqlService.allCategories().subscribe((categories) => {
      this.availableCategories = categories.filter((c) => !c.parentId);
    });

    if (data?.company) {
      const c = data.company;
      this.form.patchValue({
        companyName: c.companyName || '',
        contactPersonName: c.contactPersonName || '',
        email: c.email || '',
        uid: c.uid || '',
        website: c.website || '',
        phone: c.phone || '',
        address: {
          streetAndNumber: c.address?.streetAndNumber || '',
          city: c.address?.city || '',
          postalCode: c.address?.postalCode || '',
        },
        categoryIds:
          c.categoryIds && c.categoryIds.length > 0 ? c.categoryIds : [],
      });
      // Lock email field by default in edit mode
      const emailControl = this.form.get('email');
      emailControl?.disable({ emitEvent: false });
      this.emailEditingEnabled = false;
    }
  }

  private validateEmail(email: string) {
    // In edit mode, only validate if user explicitly enabled email editing
    if (this.data?.company && !this.emailEditingEnabled) return;

    const control = this.form.get('email');
    if (!control) return;
    const trimmed = (email || '').trim();
    // Only proceed with async availability when sync validators pass
    if (!trimmed || !control.valid) {
      // If there was a previous async error, remove only that key
      const errors = control.errors as ValidationErrors | null;
      if (errors && (errors as Record<string, unknown>)['emailTaken']) {
        const rest: ValidationErrors = { ...errors } as ValidationErrors;
        delete (rest as Record<string, unknown>)['emailTaken'];
        control.setErrors(Object.keys(rest).length ? rest : null);
      }
      return;
    }
    if (trimmed === this.lastValidatedEmail) return;
    this.lastValidatedEmail = trimmed;
    this.userService.validateCompanyEmail(trimmed).subscribe({
      next: (res) => {
        if (!res.available) {
          control.setErrors({ emailTaken: true });
        } else {
          // preserve other errors if any
          const errors = control.errors as ValidationErrors | null;
          if (errors) {
            const rest: ValidationErrors = { ...errors } as ValidationErrors;
            delete (rest as Record<string, unknown>)['emailTaken'];
            control.setErrors(Object.keys(rest).length ? rest : null);
          } else {
            control.setErrors(null);
          }
        }
      },
      error: () => {
        // On error, do not block saving due to validator
      },
    });
  }

  enableEmailEditing(): void {
    const control = this.form.get('email');
    if (!control) return;
    this.emailEditingEnabled = true;
    control.enable({ emitEvent: true });
    // Reset last validated email to ensure fresh async check
    this.lastValidatedEmail = '';
    // Trigger validation flow for current value
    const current = (control.value as string) || '';
    control.markAsDirty();
    control.markAsTouched();
    this.validateEmail(current);

    control.updateValueAndValidity();
  }

  cancel(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (this.form.invalid) return;
    const emailControl = this.form.get('email');
    if (emailControl?.hasError('emailTaken')) return;
    const value = this.form.getRawValue();
    let status = this.data?.company?.status;

    if (!status) {
      status = CompanyInvitationStatus.CREATED_NO_EMAIL;
    }
    const company: CompanyUser = new CompanyUser({
      companyName: value.companyName,
      contactPersonName: value.contactPersonName,
      email: value.email,
      uid: value.uid,
      website: value.website,
      phone: value.phone,
      address: {
        streetAndNumber: value.address.streetAndNumber,
        city: value.address.city,
        postalCode: value.address.postalCode,
      },
      categoryIds: value.categoryIds || [],
      acceptedPrivacyPolicy: true,
      acceptedAgb: true,
      password: '',
      status: status,
    });

    this.dialogRef.close({ company });
  }
}
